/**
 * AULA gamification config (Duolingo-like) on top of `gamification-core`.
 *
 * The kit is AGNOSTIC: here Aula brings ITS rules (XP for completing lessons,
 * daily study streak, badges, quests). Everything is pure → computed
 * local-first on the client for immediate feedback, and the server
 * re-verifies it (grading.rs).
 */

import { counterAtLeast, levelAtLeast, streakAtLeast } from "../gamification/badges";
import type { GamificationConfig } from "../gamification/engine";

/** Aula domain event types. */
export const AULA_EVENTS = {
  lessonCompleted: "lesson.completed",
  lessonPerfect: "lesson.perfect",
  courseCompleted: "course.completed",
} as const;

/** Bonus for a perfect lesson (score = 1). */
export const PERFECT_BONUS_XP = 5;
/** XP for completing a whole course. */
export const COURSE_COMPLETE_XP = 50;
/** Base XP if a lesson does not declare `xp_reward`. */
export const DEFAULT_LESSON_XP = 10;
/** Daily quest goal: lessons per day. */
export const DAILY_LESSON_GOAL = 3;

/**
 * Aula config. A lesson's XP travels in `event.value` (the lesson's
 * authoritative `xp_reward`); perfect lessons and completed courses add on.
 */
export const aulaGamificationConfig: GamificationConfig = {
  xp: {
    [AULA_EVENTS.lessonCompleted]: (e) => e.value ?? DEFAULT_LESSON_XP,
    [AULA_EVENTS.lessonPerfect]: PERFECT_BONUS_XP,
    [AULA_EVENTS.courseCompleted]: COURSE_COMPLETE_XP,
  },
  // Studying (completing any lesson) keeps the "study" streak alive.
  streakLines: {
    [AULA_EVENTS.lessonCompleted]: ["study"],
  },
  badges: [
    {
      id: "first-lesson",
      title: "Primera lección",
      when: counterAtLeast(AULA_EVENTS.lessonCompleted, 1),
    },
    {
      id: "ten-lessons",
      title: "10 lecciones",
      when: counterAtLeast(AULA_EVENTS.lessonCompleted, 10),
    },
    {
      id: "hundred-lessons",
      title: "100 lecciones",
      when: counterAtLeast(AULA_EVENTS.lessonCompleted, 100),
      bonusXp: 100,
    },
    { id: "streak-7", title: "Racha de 7 días", when: streakAtLeast("study", 7), bonusXp: 20 },
    { id: "streak-30", title: "Racha de 30 días", when: streakAtLeast("study", 30), bonusXp: 100 },
    {
      id: "first-course",
      title: "Primer curso completado",
      when: counterAtLeast(AULA_EVENTS.courseCompleted, 1),
    },
    { id: "scholar", title: "Estudiante (nivel 5)", when: levelAtLeast(5) },
    { id: "sage", title: "Sabio (nivel 10)", when: levelAtLeast(10) },
  ],
  quests: [
    {
      id: "daily-lessons",
      title: "3 lecciones hoy",
      period: "daily",
      goals: [{ metric: AULA_EVENTS.lessonCompleted, target: DAILY_LESSON_GOAL }],
      bonusXp: 15,
    },
  ],
};
